import { useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import {
  useDeliveryExplainFetcher,
  useIncidentNotifications,
  type DeliveryExplanation,
  type IncidentNotificationEvent,
} from '@/hooks/useNotifications.ts';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import { statusTone } from './deliveryStatus.ts';

/**
 * Who was notified about this incident, and who was not — with the reason.
 *
 * Two calls with deliberately different costs, mirroring the split the suppression
 * endpoints established in Phase 0: the list is one indexed read and loads with the
 * page, while "why didn't my rule fire?" re-runs the matcher over every rule the
 * caller holds and is only fetched when someone asks.
 *
 * Note that a rule which simply did not match writes no delivery row — otherwise the
 * log would grow with every rule anyone ever wrote. Non-matches live in the
 * explanation, which is exactly the on-demand half.
 */

const EVENT_LABELS: Record<string, string> = {
  'incident.opened': 'Opened',
  'incident.severity_changed': 'Severity changed',
  'incident.resolved': 'Resolved',
};

function EventRow({ event }: { event: IncidentNotificationEvent }) {
  const [expanded, setExpanded] = useState(false);
  const [explanation, setExplanation] = useState<DeliveryExplanation | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const explain = useDeliveryExplainFetcher();

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (!next || explanation) return;
    setLoading(true);
    setFailed(false);
    try {
      setExplanation(await explain(event.eventId));
    } catch {
      // Without this the drawer falls through to its empty state and asserts
      // "you have no notification rules" — a confident, wrong statement about the
      // user's own configuration, which is the same class of lie as the "No rules
      // matched" the router fixes were written to eliminate. Not knowing and
      // having nothing are different answers.
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <li className="rounded-md border border-border">
      <div className="flex items-start justify-between gap-2 px-3 py-2">
        <div className="min-w-0">
          <p className="text-xs font-medium">
            {EVENT_LABELS[event.eventType] ?? event.eventType}
            <span className="ml-1.5 font-normal text-muted-foreground">{event.severity}</span>
          </p>
          {event.deliveries.length === 0 ? (
            <p className="text-xs text-muted-foreground mt-1">No rules matched.</p>
          ) : (
            <ul className="mt-1 space-y-1">
              {event.deliveries.map((d) => (
                <li key={d.id} className="flex items-center gap-1.5 text-xs">
                  <Badge variant={statusTone(d.status)} className="text-[10px]">
                    {d.statusLabel}
                  </Badge>
                  <span className="text-muted-foreground">
                    {d.channel === 'in_app' ? 'in-app' : d.channel}
                    {d.destinationLabel ? ` · ${d.destinationLabel}` : ''}
                    {d.isMine ? ' · you' : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button variant="ghost" size="sm" className="shrink-0" onClick={() => { void toggle(); }}>
          {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          Why?
        </Button>
      </div>

      {expanded ? (
        <div className="border-t border-border px-3 py-2">
          {loading ? (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" /> Checking your rules…
            </p>
          ) : failed ? (
            <p className="text-xs text-muted-foreground">
              Couldn't check your rules just now. Try again in a moment.
            </p>
          ) : explanation && explanation.verdicts.length > 0 ? (
            <ul className="space-y-1">
              {explanation.verdicts.map((v) => (
                <li key={v.ruleId} className="text-xs text-muted-foreground">
                  <span className={v.matched ? 'text-foreground' : ''}>
                    {v.channel === 'in_app' ? 'In-app' : v.channel} rule
                  </span>
                  {' — '}
                  {v.reason}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">
              You have no notification rules in this organization, so nothing could have reached you.
            </p>
          )}
        </div>
      ) : null}
    </li>
  );
}

export function IncidentNotifications({ incidentId }: { incidentId: string | undefined }) {
  const { data, isLoading } = useIncidentNotifications(incidentId);

  if (isLoading) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading…
      </p>
    );
  }

  if (!data || data.length === 0) {
    return <p className="text-xs text-muted-foreground">Nothing has been sent about this incident yet.</p>;
  }

  return (
    <ul className="space-y-2">
      {data.map((event) => (
        <EventRow key={event.eventId} event={event} />
      ))}
    </ul>
  );
}
